/**
 * Account Page JavaScript
 *
 * This module handles the account page functionality for authenticated Firebase users.
 * It listens for user authentication events from the FirebaseEmailLinkAuth module
 * and updates the UI accordingly.
 *
 * Features:
 * - Display user email and submission credits
 * - Show/hide authenticated UI elements
 * - Handle page loader
 *
 * @requires firebase (global Firebase SDK instance)
 * @requires site (global WordPress site configuration object)
 */

import { initializeFirebase, isEmailSignInLink, createPageLoaderManager, hidePageLoader } from './utils.js';

(function() {
  'use strict';

  // Initialize Firebase app with configuration from WordPress
  // The site object is localized from WordPress via wp_localize_script
  // Use shared utility function to handle initialization safely
  initializeFirebase('account.js');

  // Check if the current URL is a Firebase email sign-in link
  // This detects when user is returning from clicking a sign-in link in their email
  // Use shared utility function
  const isEmailLinkFlow = isEmailSignInLink();

  // Track if we're in an email link sign-in flow
  // This flag persists even if Firebase cleans the URL before events fire
  // Set to true if email link is detected, cleared when userDataLoaded fires
  let isProcessingEmailLink = isEmailLinkFlow;

  // Get reference to the Firebase Email Link Auth container
  // This is where the auth module emits its events
  const firebaseAuthContainer = document.getElementById('firebase-email-link-auth');

  if (!firebaseAuthContainer) {
    console.error('account.js: Firebase auth container not found');
    return;
  }

  // Cache DOM element references for better performance
  // These elements are defined in page-account.twig
  const signedInAsContainer = document.querySelector('.signed-in-as-container');
  const signedInAsEmail = document.querySelector('.signed-in-as');
  const signedInAsCredits = document.querySelector('.signed-in-as-credits');
  const signOutButtonContainer = document.querySelector('.sign-out-button-container');
  const subscriptionContainer = document.getElementById('subscription-container');
  const subscriptionCards = document.querySelectorAll('.subscription-card');
  const manageSubscriptionBtn = document.getElementById('manage-subscription-btn');

  /**
   * Update signed-in user display
   * Updates all elements with the signed-in user information
   * @param {Object} userData - User data object
   */
  function updateSignedInDisplay(userData) {
    if (!userData) return;

    if (signedInAsEmail) {
      signedInAsEmail.textContent = userData.email;
    }

    // Show "Unlimited submission credits remaining" when API indicates free submissions; otherwise numeric credits
    if (signedInAsCredits) {
      signedInAsCredits.textContent = userData.free_submissions
        ? 'Unlimited submission credits remaining'
        : (userData.submission_credits || 0);
    }

    if (signedInAsContainer) {
      signedInAsContainer.classList.remove('d-none');
    }

    if (signOutButtonContainer) {
      signOutButtonContainer.classList.remove('d-none');
    }

    // Show subscription card if user has active subscription
    if (subscriptionCards && userData.stripe_subscription_status === 'active') {
      subscriptionCards.forEach(function(card) {
        if (card.dataset.productId === userData.stripe_subscription_product_id) {
          card.classList.remove('d-none');
        }
      });
    }

    // Show subscription container if user has active subscription
    if (subscriptionContainer && userData.stripe_subscription_status === 'active') {
      subscriptionContainer.classList.remove('d-none');
    }
  }

  /**
   * Reset signed-in display when user logs out
   * Clears user information and hides signed-in elements
   */
  function resetSignedInDisplay() {
    if (signedInAsContainer) {
      signedInAsContainer.classList.add('d-none');
    }

    if (signedInAsEmail) {
      signedInAsEmail.textContent = '';
    }

    if (signedInAsCredits) {
      signedInAsCredits.textContent = '0';
    }
  }

  // Create page loader manager using shared utility
  // This ensures the loader is only hidden once, preventing race conditions
  const pageLoader = createPageLoaderManager();

  /**
   * Handle user data loaded event
   * This is emitted by FirebaseEmailLinkAuth when user signs in and data is fetched
   * Fired when user is authenticated AND user data is fetched from API
   * @param {CustomEvent} event - The custom event with user data
   */
  window.addEventListener('firebase:userDataLoaded', function(event) {
    const { user, userData } = event.detail;
    console.log('account.js: User data loaded', userData);

    // Clear email link processing flag now that we have user data
    // This allows future userDataCleared events to render normally
    isProcessingEmailLink = false;

    // Update signed-in display with user information
    updateSignedInDisplay(userData);

    pageLoader.hide();
  });

  /**
   * Handle user data cleared event
   * This is emitted by FirebaseEmailLinkAuth when user signs out
   * Fired when no user is found or user signs out
   * IMPORTANT: During email link sign-in flow, this can fire BEFORE userDataLoaded
   * We must check for email link flow and skip rendering to prevent flashing wrong state
   * @param {CustomEvent} event - The custom event
   */
  firebaseAuthContainer.addEventListener('firebase:userDataCleared', function(event) {
    console.log('account.js: User data cleared');

    // If we're processing an email link sign-in flow, don't render yet
    // The userDataLoaded event will fire next and render the correct state
    // This prevents flashing cleared state before signed-in state
    // We use isProcessingEmailLink flag instead of isEmailLinkFlow because
    // Firebase may clean the URL before this event fires
    if (isProcessingEmailLink) {
      console.log('account.js: Skipping render during email link flow - waiting for userDataLoaded');
      return;
    }

    // Reset signed-in display when user logs out
    resetSignedInDisplay();
    pageLoader.hide();

    // The FirebaseEmailLinkAuth module will handle the page reload
    // after sign-out is complete
  });

  /**
   * Handle user data error event
   * This is emitted by FirebaseEmailLinkAuth when API fetch fails
   * Fired if API call to fetch user data fails
   * @param {CustomEvent} event - The custom event with error details
   */
  window.addEventListener('firebase:userDataError', function(event) {
    console.error('account.js: Error loading user data', event.detail.error);

    // Hide the page loader on error
    pageLoader.hide();

    // Optionally show an error message to the user
    // For now, we just log it to the console
  });

  /**
   * Check initial auth state and hide loader appropriately
   * This handles multiple scenarios:
   * 1. User is not signed in - hide loader immediately (auth form will show)
   * 2. User is signed in and data already loaded - hide loader (check DOM state)
   * 3. User is signed in but data not loaded yet - wait for userDataLoaded event
   */
  function checkInitialAuthState() {
    try {
      // const auth = firebase.auth();
      // const currentUser = auth.currentUser;

      // If no user is signed in, hide the loader immediately
      // The auth form will be visible, so no need to keep the loader showing
      // if (!currentUser) {
      //   console.log('account.js: No user signed in, hiding loader');
      //   if (window.pageLoader) {
      //     window.pageLoader.hide();
      //   }
      //   return;
      // }

      // If user is signed in, check if user data is already displayed
      // This handles the case where the event fired before we set up listeners
      if (signedInAsContainer && !signedInAsContainer.classList.contains('d-none')) {
        // User data is already displayed, hide the loader
        console.log('account.js: User data already displayed, hiding loader');
        setTimeout(function() {
          hidePageLoader();
        }, 1000);
        return;
      }

      // User is signed in but data not loaded yet
      // The userDataLoaded event will handle hiding the loader
      console.log('account.js: User signed in, waiting for userDataLoaded event');
    } catch (error) {
      console.error('account.js: Error checking auth state', error);
      // On error, hide loader anyway to prevent stuck spinner
      hidePageLoader();
    }
  }

  // Check initial state after a delay to allow auth module to initialize
  // This handles the race condition where auth state might be checked before auth module is ready
  setTimeout(checkInitialAuthState, 500);

  // Fallback: Hide page loader after timeout
  // This ensures the loader is hidden even if no auth events fire
  // Increased timeout to allow for API calls to complete (but not too long)
  setTimeout(function() {
    hidePageLoader();
  }, 2000);

  /**
   * Handle click on "Manage your Subscription" button
   * Creates a Stripe Customer Portal session and redirects the user to it
   */
  if (manageSubscriptionBtn) {
    manageSubscriptionBtn.addEventListener('click', function() {
      // Get the current Firebase user
      // We need the user to get an authentication token for the API call
      const auth = firebase.auth();
      const currentUser = auth.currentUser;

      // Check if user is authenticated
      // The portal requires an authenticated user with a Stripe customer ID
      if (!currentUser) {
        alert('Please sign in to manage your subscription.');
        return;
      }

      // Disable the button to prevent multiple clicks
      // This provides visual feedback and prevents duplicate requests
      const originalButtonText = manageSubscriptionBtn.textContent;
      manageSubscriptionBtn.disabled = true;
      manageSubscriptionBtn.textContent = 'Loading...';

      // Show page loader while creating the portal session
      // This provides visual feedback that something is happening
      pageLoader.show();

      // Get Firebase auth token for API authentication
      // This token is required by the backend to verify the user's identity
      currentUser.getIdToken().then(function(accessToken) {
        // Construct absolute return URL for after the portal session ends
        // Stripe requires absolute URLs for redirect purposes
        // The user will be redirected back to the account page after managing their subscription
        const returnUrl = window.location.origin + window.location.pathname;

        // Create the customer portal session via API
        // This endpoint creates a Stripe Customer Portal session and returns the portal URL
        fetch('/wp-json/showlist/v1/stripe/customer-portal-session', {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer ' + accessToken,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            return_url: returnUrl
          })
        })
        .then(async function(response) {
          // Check if the API request was successful
          if (!response.ok) {
            // Try to extract and handle API error message in JSON response
            let errorMessage = 'An error occurred while creating the portal session.';
            let errorJSON = null;
            try {
              errorJSON = await response.json();
            } catch (e) {
              // Ignore parse error, will use default error message
            }
            if (errorJSON && errorJSON.message) {
              errorMessage = errorJSON.message;
            } else if (response.status === 401) {
              errorMessage = 'Please sign in to manage your subscription.';
            } else if (response.status === 400) {
              errorMessage = 'Unable to create portal session. Please ensure you have an active subscription.';
            } else if (response.status === 500) {
              errorMessage = 'Server error. Please try again later.';
            }
            alert(errorMessage);
            throw new Error(errorMessage);
          }
          return response.json();
        })
        .then(function(responseJSON) {
          // On successful session creation, redirect to Stripe Customer Portal
          // The response contains the portal URL from Stripe
          if (responseJSON && responseJSON.url) {
            window.location.href = responseJSON.url;
          } else {
            // If response doesn't have URL, show error
            alert('Error: Invalid response from server. Please try again.');
            manageSubscriptionBtn.disabled = false;
            manageSubscriptionBtn.textContent = originalButtonText;
            hidePageLoader();
          }
        })
        .catch(function(error) {
          // Handle any errors that occurred during the process
          console.error('Error creating customer portal session:', error);
          // Button will be re-enabled below
          hidePageLoader();
        })
        .finally(function() {
          // Re-enable the button if redirect didn't happen
          // If redirect happened, this won't execute, which is fine
          // We check if button still exists and is disabled as a safety check
          if (manageSubscriptionBtn && manageSubscriptionBtn.disabled) {
            manageSubscriptionBtn.disabled = false;
            manageSubscriptionBtn.textContent = originalButtonText;
          }
        });
      }).catch(function(error) {
        // Handle error getting Firebase token
        console.error('Error getting Firebase token:', error);
        alert('Authentication error. Please try signing in again.');
        manageSubscriptionBtn.disabled = false;
        manageSubscriptionBtn.textContent = originalButtonText;
        hidePageLoader();
      });
    });
  }

})();

